import { FormInstance, message, Modal} from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';

import {
  DEFAULT_LANGUAGE,
  DEFAULT_ORIENTATION,
  POSTER_LANGUAGE_OPTIONS,
  THUMBNAIL_UPLOAD_STATE,
} from 'constants/index';
import { contentService } from 'content/service';
import { ThumbnailInterface, UploadThumbnailInterface } from 'content/type';
import { Dispatch, SetStateAction } from 'react';
import { PosterInterface } from 'type/seasonType';

// Function to normalize language value (convert lowercase/various formats to option format)
const normalizeLanguage = (lang: string | undefined): string | undefined => {
  if (!lang) return undefined;

  const normalized = lang.toUpperCase();

  // Special case handling
  const mapping: Record<string, string> = {
    KR: 'KO',
    JP: 'JA',
    ZH: 'ZH-HANS',
  };

  // Direct match case
  if (POSTER_LANGUAGE_OPTIONS.some(opt => opt.value === normalized)) {
    return normalized;
  }

  // Mapping required case
  if (mapping[normalized]) {
    return mapping[normalized];
  }

  // Return KO as default
  return 'KO';
};

interface ThumbnailHandlersProps {
  form: FormInstance;
  contentId: number;
  selectedThumbnail: ThumbnailInterface | null;
  setNewThumbnailList: (thumbnailList: ThumbnailInterface[]) => void;
  setUploadedThumbnailList: (thumbnailList: ThumbnailInterface[]) => void;
  setSelectedThumbnail: (thumbnail: ThumbnailInterface | null) => void;
  removedUids: string[];
  setRemovedUids: Dispatch<SetStateAction<string[]>>;
}

interface ThumbnailHandlers {
  handleChange: (info: UploadChangeParam<UploadFile<UploadThumbnailInterface>>) => void;
  handleNewThumbnailRemove: (thumbnail: ThumbnailInterface) => void;
  handleUploadedThumbnailRemove: (thumbnail: ThumbnailInterface) => void;
  handleThumbnailUpload: () => void;
  handleThumbnailSelect: (thumbnail: ThumbnailInterface | null) => void;
  handleThumbnailChange: (thumbnail: ThumbnailInterface, key: string, value: string) => void;
  handleNewThumbnailChange: (thumbnail: ThumbnailInterface, key: string, value: string) => void;
}

const createUploadThumbnailList = (
  uploadFileList: UploadFile<UploadThumbnailInterface>[]
): ThumbnailInterface[] => {
  let fileId = 0;
  return uploadFileList.map((file: UploadFile<UploadThumbnailInterface>): ThumbnailInterface => {
    const fileUrl = file.originFileObj ? URL.createObjectURL(file.originFileObj) : '';
    return {
      id: fileId++,
      uid: file.uid,
      file: fileUrl,
      is_representative: false,
      state: THUMBNAIL_UPLOAD_STATE.UPLOADING,
      originFileObj: file.originFileObj,
      orientation: DEFAULT_ORIENTATION,
      language: DEFAULT_LANGUAGE,
    };
  });
};

const handleChange =
  (props: ThumbnailHandlersProps) =>
  (info: UploadChangeParam<UploadFile<UploadThumbnailInterface>>): void => {
    const { form, setNewThumbnailList, removedUids } = props;

    // 1) Get existing newPosterList from form
    const existingList: PosterInterface[] = form.getFieldValue('newThumbnailList') || [];

    // 2) Extract uid array from existing list
    const existingUids = existingList.map(p => p.uid).filter((uid): uid is string => !!uid);

    // 3) Filter out removed uids and already existing uids from info.fileList
    const filtered = (info.fileList || []).filter(
      file => !removedUids.includes(file.uid) && !existingUids.includes(file.uid)
    );

    // 4) Convert only newly uploaded files to PosterInterface
    const uploadThumbnailList = createUploadThumbnailList(
      filtered as UploadFile<UploadThumbnailInterface>[]
    );

    // 5) Combine with existing list to create final list
    const combined = [...existingList, ...uploadThumbnailList];

    // 6) Sync form and state
    form.setFieldValue('newThumbnailList', combined);
    setNewThumbnailList(combined);
  };

const handleNewThumbnailRemove =
  (props: ThumbnailHandlersProps) =>
  (removedThumbnail: ThumbnailInterface): void => {
    const { form, setNewThumbnailList } = props;
    const newThumbnailList = form.getFieldValue('newThumbnailList') || [];

    const tempNewThumbnailList = newThumbnailList.filter(
      (thumbnail: { uid: string }) => thumbnail.uid !== removedThumbnail.uid
    );

    form.setFieldValue('newThumbnailList', tempNewThumbnailList);
    setNewThumbnailList(tempNewThumbnailList);
  };

const handleUploadedThumbnailRemove =
  (props: ThumbnailHandlersProps) =>
  (removedThumbnail: ThumbnailInterface): void => {
    const { form, setUploadedThumbnailList } = props;
    const uploadedThumbnailList = form.getFieldValue('uploadedThumbnailList') || [];

    const tempUploadedThumbnailList = uploadedThumbnailList.filter(
      (thumbnail: { id: number }) => thumbnail.id !== removedThumbnail.id
    );

    form.setFieldValue('uploadedThumbnailList', tempUploadedThumbnailList);
    setUploadedThumbnailList(tempUploadedThumbnailList);
  };

const handleThumbnailSelect =
  (props: ThumbnailHandlersProps) =>
  (newSelectedThumbnail: ThumbnailInterface | null): void => {
    const { form, selectedThumbnail, setSelectedThumbnail } = props;
    if (selectedThumbnail) {
      selectedThumbnail.is_representative = false;
    }
    if (newSelectedThumbnail) {
      newSelectedThumbnail.is_representative = true;
    }
    form.setFieldValue('selectedThumbnail', newSelectedThumbnail);
    setSelectedThumbnail(newSelectedThumbnail);
  };

const handleThumbnailUpload = (props: ThumbnailHandlersProps) => async (): Promise<void> => {
  const { form, contentId } = props;
  const loadingMessage = message.loading('Updating...', 0);
  const thumbnailList = form.getFieldValue('thumbnailList');
  const selectedThumbnail = form.getFieldValue('selectedThumbnail');
  const newThumbnailList: ThumbnailInterface[] = form.getFieldValue('newThumbnailList') || [];
  const uploadedThumbnailList: ThumbnailInterface[] =
    form.getFieldValue('uploadedThumbnailList') || [];

  // Normalize language values
  const normalizedNewThumbnailList = newThumbnailList.map(thumb => ({
    ...thumb,
    language: normalizeLanguage(thumb.language),
  }));
  const normalizedUploadedThumbnailList = uploadedThumbnailList.map(thumb => ({
    ...thumb,
    language: normalizeLanguage(thumb.language),
  }));
  const normalizedSelectedThumbnail = selectedThumbnail
    ? { ...selectedThumbnail, language: normalizeLanguage(selectedThumbnail.language) }
    : null;

  // 2. Count combinations from both lists
  const allList = [...normalizedNewThumbnailList, ...normalizedUploadedThumbnailList];
  const comboCounts = allList.reduce<Record<string, number>>((acc, t) => {
    const key = `${t.language}-${t.orientation}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // 3. Filter combinations with count > 1 and show error message
  const duplicates = Object.entries(comboCounts)
    .filter(([_, count]) => count > 1)
    .map(([combo]) => combo);

  if (duplicates.length > 0) {
    loadingMessage();
    Modal.error({ title: 'Error', content: `Duplicate language/aspect ratio combination: ${duplicates.join(', ')}`, okText: 'Confirm' });
    return; // Exit function to prevent actual API call
  }

  if (normalizedSelectedThumbnail) {
    normalizedNewThumbnailList.map((file: { id: number; is_representative: boolean }) => {
      if (file.id === normalizedSelectedThumbnail.id) {
        file.is_representative = normalizedSelectedThumbnail.is_representative;
        normalizedSelectedThumbnail.id = -1;
      }
      return file;
    });
  }
  const removeThumbnails = thumbnailList.filter(
    (uploadedThumbnail: { id: number }) =>
      !normalizedUploadedThumbnailList.some(
        (uploadThumbnail: { id: number }) => uploadThumbnail.id === uploadedThumbnail.id
      )
  );
  const changedThumbnails = normalizedUploadedThumbnailList.filter(
    (updated: ThumbnailInterface) => {
      const original = thumbnailList.find((t: ThumbnailInterface) => t.id === updated.id);
      return (
        original &&
        (original.language !== updated.language ||
          original.orientation !== updated.orientation ||
          original.is_representative !== updated.is_representative)
      );
    }
  );

  try {
    if (normalizedNewThumbnailList.length > 0) {
      await contentService.uploadThumbnail(contentId, normalizedNewThumbnailList);
    }
    if (removeThumbnails.length > 0) {
      await contentService.removeThumbnail(contentId, removeThumbnails);
    }
    if (changedThumbnails.length > 0) {
      for (const thumbnail of changedThumbnails) {
        await contentService.updateThumbnail(contentId, thumbnail);
      }
    }
    if (normalizedSelectedThumbnail && normalizedSelectedThumbnail.id !== -1) {
      await contentService.updateThumbnail(contentId, normalizedSelectedThumbnail);
    }
    loadingMessage();
    message.success('upload success');
    window.location.reload();
    form.resetFields([
      'newThumbnailList',
      'uploadedThumbnailList',
      'selectedThumbnail',
      'thumbnailList',
    ]);
  } catch (error) {
    loadingMessage();
    console.error('Failed to update thumbnails:', error);
  }
};

const handleNewThumbnailChange =
  (props: ThumbnailHandlersProps) =>
  (targetThumbnail: UploadThumbnailInterface, key: string, value: string): void => {
    const { form, setNewThumbnailList } = props;
    const newThumbnailList = form.getFieldValue('newThumbnailList') || [];

    const updatedList = newThumbnailList.map((thumbnail: UploadThumbnailInterface) => {
      if (thumbnail.uid !== targetThumbnail.uid) return thumbnail;

      // Normalize if language field
      const normalizedValue = key === 'language' ? normalizeLanguage(value) : value;

      const updated: UploadThumbnailInterface = {
        ...thumbnail,
        [key]: normalizedValue,
      };

      return updated;
    });

    // 1. Save original data to form (keep originFileObj)
    form.setFieldValue('newThumbnailList', updatedList);

    // 2. Ensure file is blob URL for rendering
    const renderList = updatedList.map((thumbnail: UploadThumbnailInterface) => ({
      ...thumbnail,
      file:
        typeof thumbnail.file !== 'string' && thumbnail.file
          ? URL.createObjectURL(thumbnail.file)
          : thumbnail.file,
    }));

    setNewThumbnailList(renderList);
  };

const handleThumbnailChange =
  (props: ThumbnailHandlersProps) =>
  (targetThumbnail: ThumbnailInterface, key: string, value: string): void => {
    const { form, setUploadedThumbnailList } = props;
    const uploadedThumbnailList = form.getFieldValue('uploadedThumbnailList') || [];

    // Normalize if language field
    const normalizedValue = key === 'language' ? normalizeLanguage(value) : value;

    const updatedList = uploadedThumbnailList.map((thumbnail: ThumbnailInterface) =>
      thumbnail.id === targetThumbnail.id ? { ...thumbnail, [key]: normalizedValue } : thumbnail
    );

    form.setFieldValue('uploadedThumbnailList', updatedList);
    setUploadedThumbnailList(updatedList);
  };

const thumbnailHandlers = (props: ThumbnailHandlersProps): ThumbnailHandlers => ({
  handleChange: handleChange(props),
  handleNewThumbnailRemove: handleNewThumbnailRemove(props),
  handleUploadedThumbnailRemove: handleUploadedThumbnailRemove(props),
  handleThumbnailSelect: handleThumbnailSelect(props),
  handleThumbnailUpload: handleThumbnailUpload(props),
  handleThumbnailChange: handleThumbnailChange(props),
  handleNewThumbnailChange: handleNewThumbnailChange(props),
});

export default thumbnailHandlers;
